import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { BUCKET, uploadAttachment, removeAttachment } from '../lib/attach.js'

/*
 * The tile in front of an account.
 *
 * Lemoneyd puts the bank's own mark there and it is the reason its list reads
 * at a glance rather than being scanned. Those marks belong to the banks, so
 * the app does not ship any: it draws a monogram in a colour he picks, and he
 * can drop the real logo on top for the accounts he cares about.
 *
 * Logos live in the attachments bucket under their own target type, so they
 * never mix with the balance screenshots filed against the same account.
 */

export const LOGO_TARGET = 'account_logo'

/* Picked to sit on paper without shouting, and to stay apart from each other
   at tile size. */
export const ICON_COLORS = [
  '#1F6F5C', '#2E5A8C', '#8C5A2E', '#6B4B8A',
  '#A03D3D', '#3D7A8C', '#5A6B2E', '#4A4A55',
]

export function initialsOf(name) {
  const words = String(name || '?').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

/** One signed URL per account that has a logo, in one round trip. */
export async function loadLogos(accountIds) {
  if (!accountIds.length) return {}
  const { data } = await supabase
    .from('attachments')
    .select('id, target_id, url, created_at')
    .eq('target_type', LOGO_TARGET)
    .in('target_id', accountIds)
    .order('created_at', { ascending: false })
  const rows = data ?? []
  if (!rows.length) return {}

  /* Newest wins. Replacing a logo uploads a new row rather than editing one,
     so the old file can be cleaned up without a gap where there is no logo. */
  const newest = {}
  for (const r of rows) if (!newest[r.target_id]) newest[r.target_id] = r

  const paths = Object.values(newest).map((r) => r.url)
  const { data: signed } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, 3600)
  const byPath = Object.fromEntries((signed ?? []).map((s) => [s.path, s.signedUrl]))

  const out = {}
  for (const [id, r] of Object.entries(newest)) {
    out[id] = { row: r, src: byPath[r.url] ?? null }
  }
  return out
}

export default function AccountIcon({ account, logo, size = 38 }) {
  const color = account.icon_color || ICON_COLORS[0]
  return (
    <span
      className="acct-icon"
      style={{ width: size, height: size, background: logo?.src ? 'var(--screen-2)' : color }}
      aria-hidden="true"
    >
      {logo?.src
        ? <img src={logo.src} alt="" />
        : <b style={{ fontSize: size * 0.36 }}>{initialsOf(account.name)}</b>}
    </span>
  )
}

/** Colour swatches plus an upload, for the account sheet. */
export function IconPicker({ account, logo, onChanged, toast }) {
  const [color, setColor] = useState(account.icon_color || ICON_COLORS[0])
  const [busy, setBusy] = useState(false)

  useEffect(() => { setColor(account.icon_color || ICON_COLORS[0]) }, [account])

  async function pick(c) {
    setColor(c)
    const { error } = await supabase
      .from('accounts').update({ icon_color: c }).eq('id', account.id)
    if (error) toast(error.message || 'Could not save that colour.')
    else onChanged?.()
  }

  async function upload(file) {
    if (!file || busy) return
    setBusy(true)
    try {
      await uploadAttachment(LOGO_TARGET, account.id, file, 'logo')
      /* The previous logo goes only once the new one is safely in. */
      if (logo?.row) await removeAttachment(logo.row).catch(() => {})
      onChanged?.()
    } catch (e) {
      toast(e.message || 'Could not upload that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <p className="lbl" style={{ margin: '0 2px 6px' }}>Icon</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <AccountIcon account={{ ...account, icon_color: color }} logo={logo} size={44} />
        {/* .swatches is the app's existing colour picker. Reused rather than
            reinvented, so the two never drift apart. */}
        <div className="swatches" style={{ flexWrap: 'wrap', flex: 1, marginBottom: 0 }}>
          {ICON_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={'Colour ' + c}
              onClick={() => pick(c)}
              className={c === color ? 'on' : ''}
              style={{ background: c }}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <label className="btn ghost" style={{ cursor: 'pointer' }}>
          {busy ? 'Uploading…' : logo?.src ? 'Replace logo' : 'Add logo'}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = '' }}
          />
        </label>
        {logo?.row ? (
          <button type="button" className="btn ghost" disabled={busy}
            onClick={async () => {
              setBusy(true)
              try { await removeAttachment(logo.row); onChanged?.() }
              catch (e) { toast(e.message || 'Could not remove it.') }
              finally { setBusy(false) }
            }}>
            Remove
          </button>
        ) : null}
      </div>
      <p className="lbl" style={{ margin: '8px 2px 0' }}>
        The app ships no bank logos. Add your own and it replaces the monogram.
      </p>
    </div>
  )
}
